/**
 * PostHog client-side analytics
 *
 * Initialized in AnalyticsProvider. Events are dual-written from firebaseAnalytics
 * so the same funnel (join → payment success) is available in PostHog for
 * drop-off and funnel analysis.
 */

import posthog from 'posthog-js';
import { devLog } from '@/lib/utils/console';
import { isE2ETestEnvironment } from './e2e';

const POSTHOG_KEY = typeof window !== 'undefined' ? process.env.NEXT_PUBLIC_POSTHOG_KEY : undefined;

let initialized = false;

/** True when running in the browser on a local dev host (no PostHog client). */
export function isPostHogDisabledForHostname(hostname: string): boolean {
  const h = hostname.toLowerCase();
  return (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h === '::1' ||
    h === '[::1]'
  );
}

/**
 * Initialize PostHog (call once on client, e.g. in AnalyticsProvider).
 * No-op on localhost / loopback hostnames, if NEXT_PUBLIC_POSTHOG_KEY is not set,
 * or in E2E test environment.
 * Uses reverse proxy at /ph when available (same origin) so ad blockers don't block requests.
 */
export function initPostHog(): boolean {
  if (typeof window === 'undefined') return false;
  if (initialized) {
    posthog.opt_in_capturing();
    posthog.startSessionRecording();
    return true;
  }
  if (isPostHogDisabledForHostname(window.location.hostname)) return false;
  if (isE2ETestEnvironment()) return false;
  if (!POSTHOG_KEY || POSTHOG_KEY.trim() === '') return false;

  const apiHost = `${window.location.origin}/ph`;

  try {
    posthog.init(POSTHOG_KEY, {
      api_host: apiHost,
      defaults: '2025-11-30', // API version for consistency
      person_profiles: 'identified_only',
      capture_pageview: true,
      advanced_disable_flags: true,
      capture_performance: { web_vitals: true },
      // Session recording - explicitly enabled
      disable_session_recording: false,
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '[data-ph-mask]',
      },
      // Exception autocapture - wraps window.onerror and window.onunhandledrejection
      // Note: Also enable in PostHog UI -> Error Tracking settings for full functionality
      capture_exceptions: {
        capture_unhandled_errors: true,        // Capture uncaught errors
        capture_unhandled_rejections: true,    // Capture unhandled promise rejections
        capture_console_errors: false,         // Don't capture console.error (too noisy)
      },
    });
    posthog.opt_in_capturing();
    initialized = true;
    return true;
  } catch (e) {
    console.error('[PostHog] init failed', e);
    return false;
  }
}

/** Stop optional analytics immediately after consent is withdrawn. */
export function disablePostHog(): void {
  if (!initialized) return;
  try {
    posthog.stopSessionRecording();
    posthog.opt_out_capturing();
  } catch {
    // Consent changes must never break the application.
  }
}

/**
 * Capture an event in PostHog. No-op if PostHog is not initialized.
 * Used by firebaseAnalytics.trackEvent for dual-write.
 */
export function capturePostHogEvent(
  eventName: string,
  properties?: Record<string, unknown>
): void {
  if (!initialized) return;
  try {
    const clean = properties ? cleanProperties(properties) : undefined;
    posthog.capture(eventName, clean);
    if (clean && Object.keys(clean).length > 0) {
      devLog('📊 [PostHog]', eventName, clean);
    } else {
      devLog('📊 [PostHog]', eventName);
    }
  } catch (e) {
    // Don't break app on analytics errors
  }
}

/**
 * Identify the current user in PostHog.
 * Call after authentication so anonymous events are merged into the user's profile.
 * With `person_profiles: 'identified_only'`, a profile is only created once this is called.
 */
export function identifyPostHogUser(
  userId: string,
  traits?: Record<string, unknown>
): void {
  if (!initialized) return;
  try {
    posthog.identify(userId, traits ? cleanProperties(traits) : undefined);
    devLog('📊 [PostHog] identify', userId);
  } catch (e) {
    // Don't break app on analytics errors
  }
}

/**
 * Set person properties on the current PostHog user (identified or anonymous).
 * Mirrors Firebase setUserProperties for segmentation (e.g. user_type).
 */
export function setPostHogPersonProperties(
  properties: Record<string, unknown>
): void {
  if (!initialized) return;
  try {
    posthog.people.set(cleanProperties(properties));
  } catch (e) {
    // Don't break app on analytics errors
  }
}

/**
 * Reset PostHog identity. Call on sign-out so the next user on the device
 * gets a fresh anonymous ID and isn't confused with the previous user.
 */
export function resetPostHog(): void {
  if (!initialized) return;
  try {
    posthog.reset();
    devLog('📊 [PostHog] reset (logout)');
  } catch (e) {
    // Don't break app on analytics errors
  }
}

/**
 * Capture an exception in PostHog for error tracking.
 * Used in error boundaries and try-catch blocks for manual error capture.
 */
export function captureException(
  error: Error,
  additionalProperties?: Record<string, unknown>
): void {
  if (!initialized) return;
  try {
    posthog.captureException(error, cleanProperties(additionalProperties || {}));
    devLog('🚨 [PostHog] exception', error.message, additionalProperties);
  } catch (e) {
    // Don't break app on analytics errors
  }
}

function cleanProperties(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined && v !== null) out[k] = v;
  }
  return out;
}
